import {
  Viewer,
  ConstantProperty,
  ClassificationType,
  Color,
  GeoJsonDataSource,
  Resource,
} from "cesium";
import { DeferredValue, Guard } from "lib/actions/util";
import { colorbrewer } from "../utils/colorBrewer";
import { hashDigitalAsset } from "../utils/hashDigitalAsset";
import { AttachablePluginInterface } from "./CesiumWrapper";

type GeoJsonState = {
  id: string;
  status: "loading" | "ready";
  dataSource: GeoJsonDataSource | null;
};

export default class GeoJsonPlugin implements AttachablePluginInterface {
  private readonly _viewer = new DeferredValue<Viewer>();
  private _state: { [contentUri: string]: GeoJsonState } = {};

  setViewer(viewer: Viewer) {
    this._viewer.set(viewer);
  }

  detachViewer() {
    this._state = {};
    this._viewer.clear();
  }

  get(id: string) {
    Guard.require(id, "id");
    return this.getState(id)?.dataSource ?? null;
  }

  isExisting(id: string) {
    Guard.require(id, "id");
    return !!this.getState(id);
  }

  isReady(id: string) {
    Guard.require(id, "id");
    return this.getState(id)?.status === "ready";
  }

  isShowing(id: string) {
    Guard.require(id, "id");
    return !!this.get(id)?.show;
  }

  async load(id: string, resource: Resource) {
    Guard.require(id, "id");
    Guard.require(resource, "resource");
    if (this.getState(id)) return;

    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;

    this.setState(id, { id, status: "loading", dataSource: null });
    const dataSource = await this.createDataSource(resource);
    await viewer.dataSources.add(dataSource);
    this.setState(id, { id, status: "ready", dataSource });
    viewer.scene.requestRender();
  }

  show(uri: string) {
    const dataSource = this.get(uri);
    if (dataSource) {
      dataSource.show = true;
      this._viewer.current?.scene.requestRender();
    }
  }

  hide(uri: string) {
    const dataSource = this.get(uri);
    if (dataSource) {
      dataSource.show = false;
      this._viewer.current?.scene.requestRender();
    }
  }

  private getState(id: string) {
    return this._state[id] ?? null;
  }

  private setState(id: string, state: GeoJsonState) {
    this._state[id] = state;
  }

  private async createDataSource(resource: Resource) {
    const color = this.getColor(resource.url);
    const options = {
      strokeWidth: 3,
      stroke: color.brighten(0.7, new Color()),
      fill: color.withAlpha(0.75),
      clampToGround: true,
      markerColor: color,
      markerSize: 20,
      classificationType: ClassificationType.TERRAIN,
    };
    const dataSource = await GeoJsonDataSource.load(resource, options);

    //update terrain-constrained entities to ensure that they only
    //recolor the terrain
    const entities = dataSource.entities.values;
    for (const entity of entities) {
      if (entity.polygon)
        entity.polygon.classificationType = new ConstantProperty(
          ClassificationType.TERRAIN,
        );
      if (entity.polyline)
        entity.polyline.classificationType = new ConstantProperty(
          ClassificationType.TERRAIN,
        );
    }
    return dataSource;
  }

  /**
   * Hash the contentUri to get a consistent color from the Cynthia Brewer set 1.
   */
  private getColor(contentUri: string): Color {
    const csscolour = colorbrewer.Set1[9][hashDigitalAsset(contentUri, 9)];
    return Color.fromCssColorString(csscolour);
  }
}
